import { Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { SubMenu } from '@/shared/ui';
import { useHeaderHovered } from './useHeaderHovered';

// <button> instead of <div>: Chrome fires dragstart on a draggable="true" ancestor
// when drag originates from a button, but NOT from a text-containing div (even with
// user-select: none). Styled to be visually identical to the original div.
const LabelWrapper = styled('button')(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  background: 'none',
  border: 'none',
  padding: 0,
  paddingRight: theme.spacing(1),
  color: 'inherit',
  cursor: 'grab',
  textAlign: 'left',
  flexGrow: 1,
  minWidth: 0,
  display: 'block',
  outline: 'none',
  font: 'inherit',
  fontWeight: 500,
}));

const TOOLTIP_DELAY_MS = 500;

interface Props {
  field: string;
  title: string;
  description?: string;
  quickActions: (hovered: boolean) => ReactNode;
  menuOptions: Parameters<typeof SubMenu>[0]['options'];
  onMenuOptionClick: Parameters<typeof SubMenu>[0]['onOptionClick'];
}

export const HeaderCell = forwardRef(
  (
    {
      field,
      title,
      description,
      quickActions,
      menuOptions,
      onMenuOptionClick,
    }: Props,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const hovered = useHeaderHovered(field);

    return (
      // need to use fragment to allow grid expand columns width by values
      <>
        <Tooltip
          enterDelay={TOOLTIP_DELAY_MS}
          enterNextDelay={TOOLTIP_DELAY_MS}
          arrow
          title={
            <>
              <p>{title}</p>
              {description && <p>{description}</p>}
            </>
          }
        >
          <LabelWrapper>{title}</LabelWrapper>
        </Tooltip>
        <Box ref={ref} sx={{ display: 'flex', alignItems: 'center' }}>
          {quickActions(hovered)}
          <SubMenu options={menuOptions} onOptionClick={onMenuOptionClick} />
        </Box>
      </>
    );
  },
);
